import { BoldIcon, ItalicIcon, LinkIcon, ListIcon, ListOrderedIcon } from 'lucide-react'
import { useRef, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { AreaTexto } from './Campo'

/**
 * Texto con formato de las Fases 5a y 5b (descripción de una planificación y
 * de un recurso): negrita, cursiva, listas y enlaces, nada más.
 *
 * Se guarda como Markdown liviano y se dibuja con elementos de React, nunca
 * con innerHTML: lo que alguien escriba no puede inyectar nada en la página.
 * Los enlaces sólo pueden ser http(s) y se abren siempre en otra pestaña.
 *
 *   **negrita**   *cursiva*   [texto](https://…)   https://… suelto
 *   - ítem        1. ítem
 */

/* ── Lectura ────────────────────────────────────────────── */

export function TextoConFormato({
  texto,
  className,
  /** Sólo las primeras N líneas (la tarjeta de la lista). */
  lineas,
}: {
  texto: string
  className?: string
  lineas?: number
}) {
  const bloques = aBloques(texto)
  if (bloques.length === 0) return null
  return (
    <div
      className={cn(
        'flex flex-col gap-2 text-sm break-words [&_a]:underline [&_a]:underline-offset-2 [&_strong]:font-semibold',
        lineas === 2 && 'line-clamp-2',
        lineas === 3 && 'line-clamp-3',
        className,
      )}
    >
      {bloques.map((b, i) =>
        b.tipo === 'parrafo' ? (
          <p key={i}>
            {b.lineas.map((l, j) => (
              <span key={j}>
                {j > 0 && <br />}
                {enLinea(l)}
              </span>
            ))}
          </p>
        ) : b.tipo === 'lista' ? (
          <ul key={i} className="list-disc pl-5">
            {b.items.map((l, j) => (
              <li key={j}>{enLinea(l)}</li>
            ))}
          </ul>
        ) : (
          <ol key={i} className="list-decimal pl-5">
            {b.items.map((l, j) => (
              <li key={j}>{enLinea(l)}</li>
            ))}
          </ol>
        ),
      )}
    </div>
  )
}

type Bloque =
  | { tipo: 'parrafo'; lineas: string[] }
  | { tipo: 'lista'; items: string[] }
  | { tipo: 'numerada'; items: string[] }

function aBloques(texto: string): Bloque[] {
  const bloques: Bloque[] = []
  for (const cruda of texto.replace(/\r\n/g, '\n').split('\n')) {
    const linea = cruda.trimEnd()
    const ultimo = bloques.at(-1)
    const vineta = /^\s*[-*•]\s+(.*)$/.exec(linea)
    const numero = /^\s*\d+[.)]\s+(.*)$/.exec(linea)
    if (!linea.trim()) {
      bloques.push({ tipo: 'parrafo', lineas: [] })
    } else if (vineta) {
      if (ultimo?.tipo === 'lista') ultimo.items.push(vineta[1] ?? '')
      else bloques.push({ tipo: 'lista', items: [vineta[1] ?? ''] })
    } else if (numero) {
      if (ultimo?.tipo === 'numerada') ultimo.items.push(numero[1] ?? '')
      else bloques.push({ tipo: 'numerada', items: [numero[1] ?? ''] })
    } else if (ultimo?.tipo === 'parrafo') {
      ultimo.lineas.push(linea)
    } else {
      bloques.push({ tipo: 'parrafo', lineas: [linea] })
    }
  }
  return bloques.filter((b) => b.tipo !== 'parrafo' || b.lineas.length > 0)
}

const EN_LINEA =
  /\*\*(.+?)\*\*|\*(.+?)\*|_(.+?)_|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<]+[^\s<.,;:!?)\]])/g

function enLinea(texto: string): ReactNode[] {
  const nodos: ReactNode[] = []
  let desde = 0
  for (const m of texto.matchAll(EN_LINEA)) {
    const i = m.index
    if (i > desde) nodos.push(texto.slice(desde, i))
    const [, negrita, cursiva1, cursiva2, etiqueta, destino, suelto] = m
    if (negrita) nodos.push(<strong key={i}>{enLinea(negrita)}</strong>)
    else if (cursiva1 ?? cursiva2)
      nodos.push(<em key={i}>{enLinea(cursiva1 ?? cursiva2 ?? '')}</em>)
    else if (etiqueta && destino)
      nodos.push(
        <Enlace key={i} href={destino}>
          {etiqueta}
        </Enlace>,
      )
    else if (suelto)
      nodos.push(
        <Enlace key={i} href={suelto}>
          {suelto}
        </Enlace>,
      )
    desde = i + m[0].length
  }
  if (desde < texto.length) nodos.push(texto.slice(desde))
  return nodos
}

function Enlace({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      // Siempre en el azul del calendario, aunque el texto vaya en gris (DEC-24).
      data-enlace
      className="text-hoy hover:decoration-2"
      onClick={(e) => {
        // Dentro de una tarjeta clicable, el enlace no abre la tarjeta.
        e.stopPropagation()
      }}
    >
      {children}
    </a>
  )
}

/* ── Escritura ──────────────────────────────────────────── */

type AccionFormato = 'negrita' | 'cursiva' | 'lista' | 'numerada' | 'enlace'

const BOTONES: { accion: AccionFormato; etiqueta: string; icono: ReactNode }[] = [
  {
    accion: 'negrita',
    etiqueta: 'Negrita',
    icono: <BoldIcon className="size-4" aria-hidden />,
  },
  {
    accion: 'cursiva',
    etiqueta: 'Cursiva',
    icono: <ItalicIcon className="size-4" aria-hidden />,
  },
  {
    accion: 'lista',
    etiqueta: 'Lista',
    icono: <ListIcon className="size-4" aria-hidden />,
  },
  {
    accion: 'numerada',
    etiqueta: 'Lista numerada',
    icono: <ListOrderedIcon className="size-4" aria-hidden />,
  },
  {
    accion: 'enlace',
    etiqueta: 'Enlace',
    icono: <LinkIcon className="size-4" aria-hidden />,
  },
]

interface EditorTextoProps {
  id: string
  valor: string
  alCambiar: (v: string) => void
  tope: number
  invalido?: boolean
  describedBy?: string | undefined
  placeholder?: string
}

/**
 * Área de texto con una barra mínima que escribe el formato por la persona:
 * no hace falta saber Markdown, pero quien lo sabe puede tipearlo directo.
 */
export function EditorTexto({
  id,
  valor,
  alCambiar,
  tope,
  invalido,
  describedBy,
  placeholder,
}: EditorTextoProps) {
  const area = useRef<HTMLTextAreaElement>(null)

  const envolver = (antes: string, despues: string, relleno: string) => {
    const el = area.current
    if (!el) return
    const { selectionStart: a, selectionEnd: b } = el
    const elegido = valor.slice(a, b) || relleno
    const nuevo = valor.slice(0, a) + antes + elegido + despues + valor.slice(b)
    alCambiar(nuevo)
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(a + antes.length, a + antes.length + elegido.length)
    })
  }

  const prefijarLineas = (marca: (n: number) => string) => {
    const el = area.current
    if (!el) return
    const { selectionStart: a, selectionEnd: b } = el
    const inicio = valor.lastIndexOf('\n', a - 1) + 1
    const bloque = valor.slice(inicio, b).split('\n')
    const nuevo =
      valor.slice(0, inicio) +
      bloque
        .map((l, i) => marca(i + 1) + l.replace(/^\s*([-*•]|\d+[.)])\s+/, ''))
        .join('\n') +
      valor.slice(b)
    alCambiar(nuevo)
    requestAnimationFrame(() => {
      el.focus()
    })
  }

  const aplicar = (accion: AccionFormato) => {
    if (accion === 'negrita') envolver('**', '**', 'texto')
    else if (accion === 'cursiva') envolver('*', '*', 'texto')
    else if (accion === 'lista') prefijarLineas(() => '- ')
    else if (accion === 'numerada') prefijarLineas((n) => `${String(n)}. `)
    else envolver('[', '](https://)', 'texto del enlace')
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div role="toolbar" aria-label="Formato" className="flex flex-wrap gap-1">
        {BOTONES.map((b) => (
          <button
            key={b.accion}
            type="button"
            aria-label={b.etiqueta}
            title={b.etiqueta}
            onClick={() => {
              aplicar(b.accion)
            }}
            className="flex size-9 items-center justify-center rounded-lg border border-borde bg-tarjeta text-tinta-suave hover:bg-lienzo hover:text-tinta focus-visible:outline-2 focus-visible:outline-tinta"
          >
            {b.icono}
          </button>
        ))}
      </div>
      <AreaTexto
        ref={area}
        id={id}
        rows={5}
        value={valor}
        maxLength={tope}
        placeholder={placeholder}
        aria-invalid={invalido}
        aria-describedby={describedBy}
        onChange={(e) => {
          alCambiar(e.target.value)
        }}
      />
    </div>
  )
}
